import React, { useState, useMemo } from 'react'
import { Form, Formik } from 'formik'
import { CommentSchema } from '~/utils/schemas/comment'
import { showDialog } from '~/components/common/Dialog'
import { dialogParams } from '~/components/common/Dialog/CustomDialog'
import commentApi from '~/services/apis/comment'
import { convertGmtToLocalTime } from '~/utils/datetime'
import { Button, Dialog, Tag } from 'bs-unified-ui'
import { Box, Grid, Theme, Typography, useTheme } from '@mui/material'
import { FastField } from '~/components/Formik2'
import { CommentProps } from './types'
import { showToast } from '~/components/common/Toast'
import { COMMENT_STATUS, COMMENT_STATUS_OPTIONS_2 } from '~/constants/comments'
import Star from '~/components/common/Star'
import { decodeHtmlEntities, convertHtmlToPlaintext } from './utils'

interface CommentFormProps {
  data: CommentProps
  onClose: () => void
  refetchData: () => void
  setLoading?: (loading: boolean) => void
}

export const CommentForm: React.FC<CommentFormProps> = ({
  data,
  onClose,
  refetchData,
  setLoading: setTableLoading,
}) => {
  const theme: Theme = useTheme()
  const [loading, setLoading] = useState(false)

  const {
    rating = 0,
    reviewer_email,
    reviewer,
    date_created_gmt,
    review,
    status,
  } = data || {}

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      await commentApi.update({
        ...data,
        ...values,
      })
      showToast('Changes saved')
      onClose()
      refetchData()
    } catch (e) {
      showDialog(dialogParams.error(e))
    }
    setLoading(false)
  }
  const handleDeleteComment = async () => {
    setTableLoading(true)
    try {
      await commentApi.update({ ...data, status: COMMENT_STATUS.TRASH })
      showToast('Changes saved')
      refetchData()
    } catch (e) {
      showDialog(dialogParams.error(e))
    }
    setTableLoading(false)
  }

  const moveToTrash = async () => {
    onClose()
    showDialog({
      ...dialogParams.deleteComment,
      onOk: () => handleDeleteComment(),
    })
  }

  const initialValues = useMemo(
    () => ({
      rating: rating || 0,
      review: convertHtmlToPlaintext(review),
    }),
    [data],
  )

  const ratingOptions = useMemo(() => {
    const arr = rating > 0 ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5]
    return arr.map((x) => ({ value: x, label: <Star readOnly value={x} /> }))
  }, [rating])

  const statusTagProps = useMemo(() => {
    const label = COMMENT_STATUS_OPTIONS_2.find(
      (s) => s.value === status,
    )?.label

    let textColor = theme.foreground.black
    if (status === COMMENT_STATUS.PENDING) {
      textColor = '#D6A647'
    } else if (status === COMMENT_STATUS.APPROVED) {
      textColor = '#0D8050'
    } else if (status === COMMENT_STATUS.SPAM) {
      textColor = '#F24822'
    }

    return { label, textColor }
  }, [status])

  return (
    <Dialog
      title="Edit comment"
      maxWidth={608}
      open={!!data}
      onClose={onClose}
      renderFooter={
        <>
          <Button
            variant="text"
            startIconName="delete-bin"
            onClick={moveToTrash}
            sx={{ mr: 'auto' }}
            disabled={loading}
          >
            Move to trash
          </Button>
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="edit-comment-form" loading={loading}>
            Save
          </Button>
        </>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={CommentSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {() => (
          <Form id="edit-comment-form">
            <Grid
              container
              spacing={2}
              sx={{
                '.MuiFormLabel-root': { mb: 1 },
              }}
            >
              <Grid item xs={12}>
                <Typography
                  variant="fs14"
                  display="block"
                  color={theme.foreground.black4}
                >
                  Status
                </Typography>
                <Tag {...statusTagProps} />
              </Grid>

              <Grid item sm={6} xs={12}>
                <Typography
                  variant="fs14"
                  display="block"
                  color={theme.foreground.black4}
                >
                  Submission time
                </Typography>
                <Typography variant="fs14" display="block">
                  {convertGmtToLocalTime(
                    date_created_gmt,
                    'MMMM DD YYYY [at] hh:mm a',
                  )
                    .replace('am', 'a.m.')
                    .replace('pm', 'p.m.')}
                </Typography>
              </Grid>

              <Grid item sm={6} xs={12}>
                <Typography
                  variant="fs14"
                  display="block"
                  color={theme.foreground.black4}
                >
                  Product
                </Typography>
                <Box>
                  <a
                    href={data?.product_permalink || '#'}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Typography
                      variant="fs14"
                      display="block"
                      sx={{ textDecoration: 'underline' }}
                    >
                      {decodeHtmlEntities(data?.product_name)}
                    </Typography>
                  </a>
                </Box>
              </Grid>

              <Grid item sm={6} xs={12}>
                <Typography
                  variant="fs14"
                  display="block"
                  color={theme.foreground.black4}
                >
                  Customer
                </Typography>
                <Typography variant="fs14" display="block">
                  {reviewer}
                </Typography>
              </Grid>

              <Grid item sm={6} xs={12}>
                <Typography
                  variant="fs14"
                  display="block"
                  color={theme.foreground.black4}
                >
                  Email
                </Typography>
                <Typography variant="fs14" display="block">
                  {reviewer_email}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  '*': {
                    WebkitTextFillColor: 'unset !important',
                  },
                }}
              >
                <FastField.Select
                  name="rating"
                  options={ratingOptions}
                  label="Rating"
                  checkedValue
                  fullWidth
                  resizePopover
                />
              </Grid>

              <Grid item xs={12}>
                <FastField.Input
                  name="review"
                  label="Comment"
                  fullWidth
                  minRows={3}
                  multiline
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
